/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-16 11:36:56
 * @ Modified by: David Helmick
 * @ Modified time: 2024-06-11 19:45:50
 * @ Description: Dialog to let user upload a file to use as a proforma
 */

import React, { useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Grid, Dialog, ListItemButton, Box } from '@mui/material'
import { calcDialogPaperSx } from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'

import hero1 from '@/assets/images/hero-bg/hero-1.jpg'
import { enqueueSnackbar } from 'notistack'
import FileUploader from '../UploadProforma/FileUploader'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import useAffiliate from '@/hooks/useAffiliate'

function UploadProformaComponent(props) {
  const { ActionsButton } = props

  const [openUploadProforma, setOpenUploadProforma] = useState(false)
  const [loader, setLoader] = useState(false)
  const handleOpenUploadProforma = () => {
    setOpenUploadProforma(true)
  }
  const handleCloseUploadProforma = () => {
    setOpenUploadProforma(false)
    setLoader(false)
  }

  const [{ affiliate, urlAffiliate }] = useAffiliate({})

  return (
    <>
      {ActionsButton && (
        <ListItemButton onClick={handleOpenUploadProforma} sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          cursor: "pointer",
          columnGap: "8px",
          "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "8px",
            minWidth: 0,
            flex: "1 1 auto",
            justifyContent: "flex-start",
            "& > .MuiBox-root:first-of-type": {
              flex: "0 0 auto",
              width: 16,
              minWidth: 16,
              marginRight: 0,
            },
          }}>
            <Box sx={{
              width: 16,
              minWidth: 16,
              flexShrink: 0,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 10,
              fontSize: "1.15rem",
              "& > svg": { width: "1em", height: "1em" },
              ".Mui-selected > &": { opacity: "1 !important" },
            }}>
              <FontAwesomeIcon icon={['far', 'up-to-line']} />
            </Box>
            <span>Upload Proforma</span>
          </Box>
        </ListItemButton>
      )}
      <Dialog
        open={openUploadProforma}
        onClose={handleCloseUploadProforma}
        maxWidth="lg"
        scroll="body"
        slotProps={{ paper: { sx: calcDialogPaperSx } }}
      >
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={
            <Box sx={{ color: '#368cff' }}>Uploading Proforma</Box>
          }
        >
          <Grid container spacing={0}>
            <Grid size={{ xl: 5 }}>
              <Box sx={[{ height: '100%', borderRadius: '0.65rem' }, {
                position: "relative",
                display: "flex",
                flexDirection: "column",
                "& > .divider": { zIndex: 7, width: "100%" },
                "& .hero-footer": { zIndex: 7 },
                "& .header-nav-wrapper": { zIndex: 27 },
                "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                borderTopLeftRadius: "inherit",
                borderTopRightRadius: "inherit",
                alignItems: "center",
                ".MuiCard-root > &": {
                  top: -1,
                  marginLeft: -1,
                  marginRight: -1,
                  marginBottom: -2,
                  zIndex: 6,
                },
                backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
              }, {
                "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
              }]}>
                <Box
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box style={{ backgroundImage: 'url(' + hero1 + ')' }} sx={[{ borderRadius: '0.65rem' }, {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    zIndex: 3,
                    opacity: 0.15,
                    filter: "grayscale(80%)",
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center center",
                    borderTopLeftRadius: "inherit",
                    borderTopRightRadius: "inherit",
                  }, {
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]} />
                  <Box sx={[{ borderRadius: '0.65rem' }, {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    zIndex: 5,
                    opacity: "0.7 !important",
                  }, {
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]} style={{ backgroundColor: '#070919' }} />
                  <Box sx={[{ justifyContent: 'center', textAlign: 'center', p: '3rem', '@media (min-width: 1440px)': { justifyContent: 'flex-start', textAlign: 'left' }, }, { position: "relative", zIndex: "7", width: "100%" }]}>
                    <Box sx={{ color: '#fff', mt: '1rem' }}>
                      <Box component="h1" sx={[{ my: '1rem', fontWeight: 700 }, { fontSize: "1.7rem", fontWeight: "300", lineHeight: "1.2" }]}>
                        Upload your own proforma report
                      </Box>
                      <Box component="p" sx={[{ mb: 0, color: 'rgba(255, 255, 255, 0.5)', }, { fontSize: "1rem !important" }]}>
                        When you upload your own file here,{' '}
                        {affiliate?.companyName || 'Housefolios'} will
                        allow users to download it on your
                        marketplace.
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xl: 7 }}>
              <FileUploader
                proformaFile={props.proformaFile}
                propertyId={props.propertyId}
                setLoader={setLoader}
                collection="proformas"
              />
            </Grid>
          </Grid>
        </BlockUi>
      </Dialog>
    </>
  )
}

export default UploadProformaComponent
